import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect } from "react";
import type { BillingStateSummary } from "@focal/ui-kit";
import {
  getBillingStateSummary,
  BILLING_STATE_SUMMARY_GET,
} from "@/api/client/billing-state";
import { useWebSocket } from "@/context/WebSocketProvider";
import { CloudEventType, CloudEvent } from "@/constants/websocket-events";

// Minimum interval between query invalidations (prevents feedback loops)
const INVALIDATION_THROTTLE_MS = 5000;

// Module-level timestamp to share throttle across all hook instances
let lastGlobalInvalidationTime = 0;

/** Boolean capabilities usable with canAccessFeature (canInviteUsers is an object) */
export type BillingCapability = Exclude<
  keyof BillingStateSummary["capabilities"],
  "canInviteUsers"
>;

export interface UseBillingStateOptions {
  enabled?: boolean;
  refetchOnWindowFocus?: boolean;
}

export const useBillingState = (options: UseBillingStateOptions = {}) => {
  const { enabled = true, refetchOnWindowFocus = true } = options;
  const queryClient = useQueryClient();
  const { subscribe } = useWebSocket();

  const throttledInvalidate = useCallback(() => {
    const now = Date.now();
    if (now - lastGlobalInvalidationTime < INVALIDATION_THROTTLE_MS) {
      return;
    }
    lastGlobalInvalidationTime = now;
    queryClient.invalidateQueries({
      queryKey: [BILLING_STATE_SUMMARY_GET],
    });
  }, [queryClient]);

  // Invalidation signal only — always refetch, never render from event data
  useEffect(() => {
    if (!enabled) return;

    const handleMessage = (event: CloudEvent) => {
      if (event.type === CloudEventType.BILLING_STATUS_UPDATED) {
        throttledInvalidate();
      }
    };

    const unsubscribe = subscribe("billing", undefined, handleMessage);

    return () => {
      unsubscribe();
    };
  }, [enabled, subscribe, throttledInvalidate]);

  const query = useQuery({
    queryKey: [BILLING_STATE_SUMMARY_GET],
    queryFn: getBillingStateSummary,
    enabled,
    refetchOnWindowFocus,
    staleTime: INVALIDATION_THROTTLE_MS,
    retry: (failureCount, error) => {
      // Don't retry on authentication errors (check for Axios error shape)
      if (
        error &&
        typeof error === "object" &&
        "response" in error &&
        error.response &&
        typeof error.response === "object" &&
        "status" in error.response &&
        error.response.status === 401
      ) {
        return false;
      }
      return failureCount < 2;
    },
  });

  // status.isValid is THE access decision; default to valid while loading
  // so features aren't flashed as disabled before data arrives
  const isValid = query.data?.status.isValid ?? true;

  const canAccessFeature = useCallback(
    (feature: BillingCapability) => {
      if (!query.data) {
        return true; // Default to allowing access while loading
      }
      return query.data.capabilities[feature];
    },
    [query.data],
  );

  return {
    billingState: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    isValid,
    canInviteUsers: query.data?.capabilities.canInviteUsers ?? {
      allowed: true,
      reason: null,
    },
    canAccessFeature,
  };
};
